// Resolves login-method selection and auth parameters from runtime config
// (window.APP_SETTINGS / public/config.js). Centralizes the legacy-compatible
// defaults so deployed config.js files that predate these keys keep behaving
// exactly as before.

export type LoginMethod = "basic" | "legacySso" | "azureSso";

export type AuthStorageMode = "auto" | "localStorage" | "sessionStorage";

const ALL_LOGIN_METHODS: readonly LoginMethod[] = ["basic", "legacySso", "azureSso"];

/** Default web SSO cookie base name — matches Triyo.Web's AUTH_COOKIE_NAME default. */
const DEFAULT_SSO_COOKIE_NAME = "triyo-token-app";

function isLoginMethod(value: unknown): value is LoginMethod {
  return typeof value === "string" && (ALL_LOGIN_METHODS as readonly string[]).includes(value);
}

// Pre-loginMethods behavior: basic always; legacy web SSO whenever appUrl is set
// (promoted to primary by loginSSO: true); Azure AD when AUTH_TYPE === "AzureAD".
function deriveLoginMethods(settings: Window["APP_SETTINGS"] | undefined): LoginMethod[] {
  const methods: LoginMethod[] = ["basic"];
  if (settings?.appUrl) {
    if (settings.loginSSO === true) {
      methods.unshift("legacySso");
    } else {
      methods.push("legacySso");
    }
  }
  if (settings?.AUTH_TYPE === "AzureAD") {
    methods.push("azureSso");
  }
  return methods;
}

/**
 * Ordered list of sign-in methods the login screen offers; the first entry is
 * the primary (emphasized) method. An explicit APP_SETTINGS.loginMethods wins;
 * unknown entries and duplicates are dropped, and "legacySso" is dropped when
 * appUrl is not configured (the method cannot work without it). An absent,
 * empty, or entirely invalid list falls back to the derived legacy behavior so
 * a config typo can never produce a login screen with no methods.
 */
export function resolveLoginMethods(): LoginMethod[] {
  const settings = window.APP_SETTINGS;
  const configured: unknown = settings?.loginMethods;
  if (Array.isArray(configured)) {
    const methods = configured
      .filter(isLoginMethod)
      .filter((method, index, all) => all.indexOf(method) === index)
      .filter((method) => method !== "legacySso" || Boolean(settings?.appUrl));
    if (methods.length > 0) {
      return methods;
    }
  }
  return deriveLoginMethods(settings);
}

export function isLoginMethodEnabled(method: LoginMethod): boolean {
  return resolveLoginMethods().includes(method);
}

/**
 * Web SSO cookie names to probe, in priority order: the configured base name
 * (AUTH_COOKIE_NAME — the same key Triyo.Web reads, so both sides can be
 * re-pointed together) followed by its "-api" variant, which the API itself
 * also writes on login.
 */
export function resolveSsoCookieNames(): [string, string] {
  const configured = window.APP_SETTINGS?.AUTH_COOKIE_NAME;
  const base =
    typeof configured === "string" && configured.trim().length > 0 ? configured.trim() : DEFAULT_SSO_COOKIE_NAME;
  return [base, `${base}-api`];
}

/**
 * Where auth tokens are persisted: "auto" (default) lets the Remember-me choice
 * pick localStorage vs sessionStorage; the other values force one container.
 */
export function resolveAuthStorageMode(): AuthStorageMode {
  const configured = window.APP_SETTINGS?.authStorage;
  return configured === "localStorage" || configured === "sessionStorage" ? configured : "auto";
}
